import { TYPOGRAPHY2 } from '@/Client/Styles/TYPOGRAPHY2';
import { dashCase } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { getScanValueOverrideTooltip, scanValuePresenter } from '@kaiizen/kai-presenters';
import {
  getEffectiveScanValue,
  getScanValueHasOverride,
  getUpdatedScanValue,
  isExpressionValue,
  ScanTemplateValue,
  ScanValue,
} from '@kaiizen/kai-scan-template-system';
import { Checkbox, FormControlLabel } from '@mui/material';
import { useScanEditingContextSlice } from '../../State';
import { LabeledInput, LabeledInputSize, LabeledInputStyle, LabeledSelect } from '../../Widgets';

const VALUE_OVERRIDDEN_STYLE = css`
  label: VALUE_OVERRIDDEN_STYLE;
  color: red;
  ${TYPOGRAPHY2.xSmall}
  margin-top: 4px;
`;

const asBoolean = (value: any) => typeof value === 'boolean' ? value : false;

// Component to display a single value
export const ScanValueView = ({
  valueKey,
  value,
  templateValue,
  updateValue,
  size = 'small',
  style = 'normal',
  customLabel,
  noLabel = false,
  backgroundColor,
  width,
  label,
  tabIndex,
  disabled = false,
  placeholderValue,
}: {
  valueKey: string;
  value?: ScanValue;
  templateValue: ScanTemplateValue;
  updateValue: (key: string, newValue: ScanValue) => void;
  size?: LabeledInputSize;
  style?: LabeledInputStyle;
  width?: string;
  noLabel?: boolean;
  backgroundColor?: string;
  customLabel?: string;
  label?: string;
  tabIndex?: number;
  disabled?: boolean;
  placeholderValue?: string | number;
}) => {
  const { scanIsEditable } = useScanEditingContextSlice(['scanIsEditable']);
  if (!templateValue) return null;

  const effectiveValue = getEffectiveScanValue(value);
  const valueType = templateValue?.type || '';
  const isTextValueType = valueType === 'text';
  if (!scanIsEditable) disabled = true;

  // Determine the state of the value
  const isOverride = getScanValueHasOverride(value);

  const valueKeyPresented = dashCase(valueKey, ' ');
  customLabel ||= valueKeyPresented;

  if (valueType === 'checkbox') {
    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={asBoolean(effectiveValue)}
            onChange={e => {
              updateValue(valueKey, getUpdatedScanValue(templateValue, value, e.target.checked));
            }}
          />
        }
        label={label ?? valueKeyPresented}
        title={isOverride ? getScanValueOverrideTooltip(value, templateValue) : undefined}
        sx={{
          // Override MUI's default margin with important
          margin: '0 !important',
          marginBottom: '0 !important',
          // Checkboxes have fixed width for multi-column layout
          width: '180px',
          minWidth: '150px',
          maxWidth: '200px',
          flexGrow: 0,
          flexShrink: 0,
          height: '44px', // Updated to iOS-friendly height
          alignItems: 'center',
        }}
      />
    );
  }

  // Handle valueOptions with select dropdown
  if (templateValue.valueOptions) {
    return (
      <LabeledSelect
        label={label ?? customLabel}
        title={templateValue?.description}
        value={effectiveValue != null ? String(effectiveValue) : ''}
        items={templateValue.valueOptions.map(option => ({ value: option.value?.toString() ?? '', label: option.label }))}
        onChange={val => {
          updateValue(valueKey, getUpdatedScanValue(templateValue, value, val));
        }}
        tabIndex={tabIndex}
      />
    );
  }

  const placeholder = scanValuePresenter(
    isExpressionValue(templateValue)
      ? ''
      : templateValue.value ?? null,
    templateValue,
  );

  return (
    <div
      style={{
        margin: '0 !important',
        marginBottom: '0 !important',
        width: isTextValueType ? '100%' : width ?? '100px',
        minWidth: isTextValueType ? '100%' : width ?? '180px',
        maxWidth: isTextValueType ? '100%' : width ?? '200px',
        flexGrow: isTextValueType ? 1 : 0,
        flexShrink: isTextValueType ? 1 : 0,
      }}
    >
      <LabeledInput
        frameColor={isOverride ? 'red' : undefined}
        label={noLabel ? undefined : label ?? (style === 'calculated' ? '' : customLabel)}
        title={templateValue?.description}
        size={size}
        backgroundColor={backgroundColor}
        // `scanValuePresenter` embeds the unit in the value string ($ for usd, a unit suffix for
        // measurements), so we must not also render a separate unit adornment (that would double it
        // to "$$12.50" / "1,234 sqft sqft"), and the field must be a text input: a number input
        // silently blanks any non-numeric string ("$12.50", "1,234 sqft"), which erased overridden
        // prices and room measurements after a scan-data sync (KAIDEV-4222). Parsing on edit already
        // strips $ and , via getNumberScanValueOrUndefined. Matches the new FocusedScopeEditor UI.
        value={scanValuePresenter(effectiveValue ?? placeholderValue ?? '', templateValue)}
        style={style === 'calculated' ? isOverride ? 'attention' : 'calculated' : style}
        onUpdate={(localValue: string) => updateValue(valueKey, getUpdatedScanValue(templateValue, value, localValue))}
        disabled={disabled}
        type={'text'}
        multiline={templateValue.type === 'text'}
        placeholder={placeholder}
        tabIndex={tabIndex}
      />
      {/* `placeholder` already carries the $/unit from scanValuePresenter — don't prepend $ again (KAIDEV-4222). */}
      {isOverride && <div css={VALUE_OVERRIDDEN_STYLE} color='red'>Default: {placeholder}</div>}
    </div>
  );
};
